import { useMemo } from "react";
import { motion } from "framer-motion";
import { useLocation } from "react-router-dom";

/**
 * OceanBackground — full-viewport cinematic ocean-of-learning backdrop.
 *
 * Instead of subject icons, we now float short, meaningful WORDS that drift
 * upward from the deep like knowledge rising to the surface. Words mix
 * English verbs and subject names in several Indian scripts to convey the
 * app's holistic, multilingual reach without any promotional copy.
 */

const WORDS = [
  { text: "Learning for Life",   accent: "#67e8f9" }, // cyan
  { text: "আরও ভালো প্রকাশ",       accent: "#a5b4fc" }, // Better expression (Bengali)
  { text: "சிறந்த கவனிப்பு",       accent: "#f9a8d4" }, // Better attention (Tamil)
  { text: "बेहतर सोच",             accent: "#fcd34d" }, // Better thinking (Hindi)
  { text: "Beyond Overfitting",  accent: "#6ee7b7" }, // emerald
  { text: "बेहतर समझ",             accent: "#fdba74" }, // Better understanding (Hindi)
  { text: "কম মুখস্থ করা",           accent: "#c4b5fd" }, // Less rote memorising (Bengali)
  { text: "சிறந்த மனப்பான்மை",     accent: "#fca5a5" }, // Better attitude (Tamil)
];

/** Deterministic pseudo-random from a seed — keeps positions stable across renders. */
function pseudoRand(seed) {
  let s = seed;
  return () => {
    s = (s * 9301 + 49297) % 233280;
    return s / 233280;
  };
}

/** Place words spread across the viewport, staggered in bands. */
function useWordPositions(topOnly) {
  return useMemo(() => {
    const rand = pseudoRand(42);
    return WORDS.map((w, i) => {
      // Phrases are longer than single words, so we use 2 columns and clamp
      // the left position so no phrase spills off the right edge.
      const col = (i % 2) * 0.42;
      const jitterX = (rand() - 0.5) * 0.08;
      const left = Math.max(0.03, Math.min(0.48, col + jitterX));
      // topOnly=true → 4 bands packed in the upper 55% of the viewport
      // otherwise → 4 bands spread across the viewport
      const bandIdx = Math.floor(i / 2);
      const bandTop = topOnly
        ? 0.05 + bandIdx * 0.12   // 0.05, 0.17, 0.29, 0.41 → all above ~0.6
        : 0.10 + bandIdx * 0.20;
      const jitterY = topOnly ? rand() * 0.06 : rand() * 0.10;
      const top = bandTop + jitterY;
      const delay = i * 1.3 + rand() * 1.5;
      const duration = 16 + rand() * 8;
      // Words drift only ~90px upward — that keeps them above the central line
      // when they start in the top band.
      const drift = 18 + rand() * 22;
      const size = 18 + Math.round(rand() * 8);
      return { ...w, left, top, delay, duration, drift, size };
    });
  }, [topOnly]);
}

/** Twinkling particle field — pure CSS keyframes via framer-motion. */
function Particles({ count = 34 }) {
  const items = useMemo(() => {
    const rand = pseudoRand(7);
    return Array.from({ length: count }).map((_, i) => ({
      id: i,
      left: rand() * 100,
      top: rand() * 100,
      size: 1 + Math.round(rand() * 2),
      delay: rand() * 6,
      duration: 3 + rand() * 4,
      opacity: 0.15 + rand() * 0.45,
    }));
  }, [count]);
  return (
    <>
      {items.map((p) => (
        <motion.span
          key={p.id}
          aria-hidden
          className="absolute rounded-full bg-white"
          style={{ left: `${p.left}%`, top: `${p.top}%`, width: p.size, height: p.size }}
          animate={{ opacity: [p.opacity * 0.3, p.opacity, p.opacity * 0.3] }}
          transition={{ duration: p.duration, delay: p.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}
    </>
  );
}

export default function OceanBackground() {
  const { pathname } = useLocation();
  const showWords = pathname === "/login";
  // Only run the animated layers on the Login page. On every other route we
  // render a fully static ocean — same visual, ~zero paint work per frame —
  // for a smoother, snappier feel while navigating tools.
  const animated = showWords;
  const words = useWordPositions(showWords);

  return (
    <div
      aria-hidden
      className="fixed inset-0 z-0 pointer-events-none overflow-hidden select-none"
    >
      {/* --- Layer 1 : cinematic gradient sea --------------------------- */}
      <div
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(180deg, #0f172a 0%, #1e293b 22%, #164e63 46%, #0e7490 66%, #0891b2 82%, #0e7490 100%)",
        }}
      />

      {/* --- Layer 2 : horizon glow (animated only on Login) ------------ */}
      {animated ? (
        <motion.div
          className="absolute left-1/2 -translate-x-1/2 rounded-full blur-3xl"
          style={{
            width: "140vmax", height: "60vmax",
            top: "20%",
            background:
              "radial-gradient(ellipse at center, rgba(56,189,248,0.35) 0%, rgba(129,140,248,0.15) 40%, rgba(15,23,42,0) 70%)",
          }}
          animate={{ opacity: [0.55, 0.9, 0.55], scale: [1, 1.05, 1] }}
          transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        />
      ) : (
        <div
          className="absolute left-1/2 -translate-x-1/2 rounded-full blur-3xl"
          style={{
            width: "140vmax", height: "60vmax",
            top: "20%", opacity: 0.75,
            background:
              "radial-gradient(ellipse at center, rgba(56,189,248,0.35) 0%, rgba(129,140,248,0.15) 40%, rgba(15,23,42,0) 70%)",
          }}
        />
      )}

      {/* --- Layer 3 : sun rays (animated only on Login) ---------------- */}
      <div className="absolute inset-0" style={{ mixBlendMode: "screen" }}>
        {[0, 1, 2].map((i) => (
          animated ? (
            <motion.div
              key={i}
              className="absolute -top-20 -right-24 h-[130vmax] w-[10vmax] origin-top-right"
              style={{
                transform: `rotate(${18 + i * 6}deg)`,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0) 100%)",
                filter: "blur(6px)",
              }}
              animate={{ opacity: [0.35, 0.8, 0.35] }}
              transition={{ duration: 6 + i * 1.5, repeat: Infinity, ease: "easeInOut", delay: i * 0.8 }}
            />
          ) : (
            <div
              key={i}
              className="absolute -top-20 -right-24 h-[130vmax] w-[10vmax] origin-top-right"
              style={{
                transform: `rotate(${18 + i * 6}deg)`,
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 45%, rgba(255,255,255,0) 100%)",
                filter: "blur(6px)",
                opacity: 0.55,
              }}
            />
          )
        ))}
      </div>

      {/* --- Layer 4 : plankton / star field (Login only) --------------- */}
      {animated && <Particles count={40} />}

      {/* --- Layer 5 : floating knowledge words (login only, top band) - */}
      {showWords && words.map((w, idx) => (
        <motion.div
          key={idx}
          className="absolute"
          style={{ left: `${w.left * 100}%`, top: `${w.top * 100}%` }}
          initial={{ opacity: 0 }}
          animate={{
            opacity: [0, 0.85, 0.85, 0],
            // Reduced upward drift so the word never crosses the mid-line
            y: [8, -30, -60, -95],
            x: [0, w.drift, -w.drift * 0.5, 0],
          }}
          transition={{ duration: w.duration, delay: w.delay, repeat: Infinity, ease: "easeInOut" }}
        >
          <span
            className="font-heading font-bold tracking-tight whitespace-nowrap select-none"
            style={{
              fontSize: `${w.size}px`,
              color: w.accent,
              letterSpacing: "-0.01em",
              textShadow: `0 0 22px ${w.accent}aa, 0 0 8px rgba(255,255,255,0.35), 0 2px 6px rgba(15,23,42,0.55)`,
            }}
          >
            {w.text}
          </span>
        </motion.div>
      ))}

      {/* --- Layer 6 : SVG wave sheets (drift horizontally) ------------- */}
      <svg
        className="absolute inset-x-0 bottom-0 w-[220%] h-[36vh] -translate-x-1/4"
        viewBox="0 0 2400 260"
        preserveAspectRatio="none"
      >
        <defs>
          <linearGradient id="waveA" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#22d3ee" stopOpacity="0.55" />
            <stop offset="1" stopColor="#0e7490" stopOpacity="0.85" />
          </linearGradient>
          <linearGradient id="waveB" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0" stopColor="#67e8f9" stopOpacity="0.35" />
            <stop offset="1" stopColor="#155e75" stopOpacity="0.85" />
          </linearGradient>
        </defs>
        {animated ? (
          <>
            <motion.path
              fill="url(#waveB)"
              d="M0,140 C300,90 600,190 900,140 C1200,90 1500,190 1800,140 C2100,90 2400,190 2400,140 L2400,260 L0,260 Z"
              animate={{ x: [-80, 0, -80] }}
              transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
            />
            <motion.path
              fill="url(#waveA)"
              d="M0,170 C300,120 600,220 900,170 C1200,120 1500,220 1800,170 C2100,120 2400,220 2400,170 L2400,260 L0,260 Z"
              animate={{ x: [40, -40, 40] }}
              transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
            />
          </>
        ) : (
          <>
            <path fill="url(#waveB)" d="M0,140 C300,90 600,190 900,140 C1200,90 1500,190 1800,140 C2100,90 2400,190 2400,140 L2400,260 L0,260 Z" />
            <path fill="url(#waveA)" d="M0,170 C300,120 600,220 900,170 C1200,120 1500,220 1800,170 C2100,120 2400,220 2400,170 L2400,260 L0,260 Z" />
          </>
        )}
      </svg>

      {/* --- Layer 7 : bottom aurora glow -------------------------------- */}
      <div
        className="absolute inset-x-0 bottom-0 h-40 blur-2xl opacity-70"
        style={{
          background:
            "radial-gradient(ellipse at 50% 100%, rgba(34,211,238,0.55) 0%, rgba(129,140,248,0.35) 40%, rgba(15,23,42,0) 75%)",
        }}
      />

      {/* --- Layer 8 : top vignette for typography contrast -------------- */}
      <div
        className="absolute inset-x-0 top-0 h-40"
        style={{
          background:
            "linear-gradient(180deg, rgba(15,23,42,0.55) 0%, rgba(15,23,42,0) 100%)",
        }}
      />
    </div>
  );
}
